import { useCallback, useEffect, useRef, useState } from 'react';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { API_URL_CTRACKER } from '@shared/api/config';

export interface SignalRMessage {
  type: 'EventAssigned' | 'UserInactivated' | 'RefreshGrid';
  eventRecord?: string;
  title?: string;
  message?: string;
  timestamp: string;
}

interface UseSignalROptions {
  userId: string;
  /** Si es true, se conecta automáticamente al montar */
  autoConnect?: boolean;
  /** Callback cuando llega un mensaje */
  onMessage?: (message: SignalRMessage) => void;
}

interface UseSignalRReturn {
  /** Estado actual de la conexión */
  connectionState: HubConnectionState;
  /** Conectar al hub */
  connect: () => Promise<void>;
  /** Desconectar del hub */
  disconnect: () => Promise<void>;
  /** Indica si está conectado */
  isConnected: boolean;
  /** Último error ocurrido */
  error: string | null;
}

const HUB_URL = `${API_URL_CTRACKER}/hubs/event-assignment`;

export function useSignalR({
  userId,
  autoConnect = true,
  onMessage,
}: UseSignalROptions): UseSignalRReturn {
  const [connectionState, setConnectionState] = useState<HubConnectionState>(
    HubConnectionState.Disconnected,
  );
  const [error, setError] = useState<string | null>(null);
  const connectionRef = useRef<HubConnection | null>(null);
  const onMessageRef = useRef(onMessage);

  // Keep ref updated
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const createConnection = useCallback(() => {
    const connection = new HubConnectionBuilder()
      .withUrl(HUB_URL)
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(LogLevel.Warning)
      .build();

    // Escuchar mensajes de asignación de evento
    connection.on('EventAssigned', (data: SignalRMessage) => {
      console.log('[SignalR] EventAssigned:', data);
      onMessageRef.current?.({ ...data, type: 'EventAssigned' });
    });

    // Escuchar mensajes de inactividad
    connection.on('UserInactivated', (data: SignalRMessage) => {
      console.log('[SignalR] UserInactivated:', data);
      onMessageRef.current?.({ ...data, type: 'UserInactivated' });
    });

    // Escuchar refresh de grid
    connection.on('RefreshGrid', (data: SignalRMessage) => {
      console.log('[SignalR] RefreshGrid:', data);
      onMessageRef.current?.({ ...data, type: 'RefreshGrid' });
    });

    // Confirmación de suscripción
    connection.on('Subscribed', (msg: string) => {
      console.log('[SignalR] Suscripción confirmada:', msg);
    });

    // Eventos de reconexión
    connection.onreconnecting(() => {
      setConnectionState(HubConnectionState.Reconnecting);
      setError('Reconectando...');
    });

    connection.onreconnected(() => {
      setConnectionState(HubConnectionState.Connected);
      setError(null);
      // Re-suscribirse al grupo del usuario después de reconectar
      connection.invoke('SubscribeToUser', userId).catch(console.error);
    });

    connection.onclose((err) => {
      setConnectionState(HubConnectionState.Disconnected);
      if (err) {
        setError(`Conexión cerrada: ${err.message}`);
      }
    });

    return connection;
  }, [userId]);

  const connect = useCallback(async () => {
    try {
      setError(null);

      if (
        connectionRef.current &&
        connectionRef.current.state !== HubConnectionState.Disconnected
      ) {
        await connectionRef.current.stop();
      }

      const connection = createConnection();
      connectionRef.current = connection;

      setConnectionState(HubConnectionState.Connecting);
      await connection.start();
      setConnectionState(HubConnectionState.Connected);

      console.log('[SignalR] Conectado exitosamente');

      await connection.invoke('SubscribeToUser', userId);
      console.log(`[SignalR] Suscrito al grupo user-${userId}`);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error desconocido';
      console.error('[SignalR] Error al conectar:', errorMessage);
      setError(errorMessage);
      setConnectionState(HubConnectionState.Disconnected);
    }
  }, [createConnection, userId]);

  const disconnect = useCallback(async () => {
    if (connectionRef.current) {
      try {
        await connectionRef.current.stop();
      } catch (err) {
        console.error('[SignalR] Error al desconectar:', err);
      }
      connectionRef.current = null;
      setConnectionState(HubConnectionState.Disconnected);
    }
  }, []);

  // Auto-connect al montar si está habilitado
  useEffect(() => {
    if (autoConnect && userId) {
      connect();
    }

    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop().catch(console.error);
        connectionRef.current = null;
      }
    };
  }, [autoConnect, userId, connect]);

  return {
    connectionState,
    connect,
    disconnect,
    isConnected: connectionState === HubConnectionState.Connected,
    error,
  };
}
